import React from 'react';
import { Link } from 'react-router-dom';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthChangeLink,
  AuthFootnote,
  AuthHint,
  AuthOtp,
  AuthResend,
  AuthSubmit,
} from '../../design-system/patterns/auth';
import { routes } from '../../routes/routes';

/**
 * The one OTP view. Two routes render it.
 *
 * WHY ONE FILE AND NOT TWO
 * ------------------------
 * `/otp-verification` and `/email-verification-otp` were 287 and 321 lines of
 * near-identical code: the same six cells, the same keyboard handling, the same
 * 60 second resend, the same clear-on-error. Everything that actually differed
 * fits in the table below plus three callbacks. Keeping them apart meant every
 * OTP fix had to be written twice, and the copies had already drifted (only one
 * of them read `?email=`, only one of them counted attempts).
 *
 * So the shape lives here once and the two containers supply:
 *   - `variant`, which selects the copy and the two escape routes,
 *   - `destination`, the address the code went to,
 *   - `onSubmit`, which is where the two flows genuinely diverge: one verifies
 *     a password-reset code and continues to /reset-password, the other
 *     verifies an email and lands the user in the app.
 *
 * Pure, like `login.view.jsx`. No fetch, no navigate, no localStorage, so the
 * state explorer mounts the real screen rather than a mock of it.
 *
 * THE MASKED DESTINATION
 * ----------------------
 * The address is the one fact that stops a user typing a code from the wrong
 * inbox, so it is on the page, ink weight, inside a muted line. It is masked
 * because an auth screen is very often open on a shared or projected screen:
 * enough of the address to recognise, never enough to read out.
 */

/** The copy and the two escape routes, per route. Nothing else differs. */
export const OTP_VARIANTS = {
  /** `/otp-verification`, the password-reset code. */
  'password-reset': {
    title: 'Enter your code',
    submitLabel: 'Verify code',
    loadingLabel: 'Verifying',
    changeTo: routes.FORGET,
    changeLabel: 'Use a different email address',
    backTo: routes.FORGET,
    backLabel: 'Back to password reset',
    footnote: (
      <>
        Wrong account? <Link to={routes.LOGIN}>Back to sign in</Link>
      </>
    ),
  },
  /** `/email-verification-otp`, the sign-up email check. */
  email: {
    title: 'Verify your email',
    submitLabel: 'Verify email',
    loadingLabel: 'Verifying',
    changeTo: routes.SIGNUP,
    changeLabel: 'Change the address on your account',
    backTo: routes.SIGNUP,
    backLabel: 'Back to sign up',
    footnote: (
      <>
        Already verified? <Link to={routes.LOGIN}>Log in to your account</Link>
      </>
    ),
  },
};

/**
 * Mask an email for display: first and last character of the local part, the
 * domain in full. The domain has to stay readable, because "did I use my work
 * address or my Gmail" is the actual question a stuck user is asking.
 *
 * A one or two character local part cannot be partly masked without giving the
 * whole thing away, so it is masked entirely.
 */
export function maskDestination(value) {
  const address = String(value || '').trim();
  const at = address.lastIndexOf('@');
  if (at < 1) return address;

  const local = address.slice(0, at);
  const domain = address.slice(at + 1);
  const dots = '•'.repeat(Math.min(Math.max(local.length - 2, 3), 5));

  if (local.length <= 2) return `${local.slice(0, 1)}${dots}@${domain}`;
  return `${local.slice(0, 1)}${dots}${local.slice(-1)}@${domain}`;
}

export const EMPTY_CODE = ['', '', '', '', '', ''];

export function OtpView({
  variant = 'email',
  destination = '',
  values = EMPTY_CODE,
  onChange = () => {},
  onSubmit = () => {},
  loading = false,
  error,
  success,
  resendSeconds = 0,
  resendLoading = false,
  onResend = () => {},
  // A counter, not a boolean. The container bumps it on mount and every time
  // it clears the code, and the effect below puts the caret back in cell one.
  // A boolean cannot express "do it again" without a reset dance.
  focusSignal,
}) {
  const copy = OTP_VARIANTS[variant] || OTP_VARIANTS.email;
  const code = values.join('');
  const complete = code.length === 6;
  const busy = loading || Boolean(success);

  const groupRef = React.useRef(null);

  React.useEffect(() => {
    if (focusSignal == null) return;
    const first = groupRef.current && groupRef.current.querySelector('input');
    if (!first) return;
    first.focus();
    if (first.select) first.select();
  }, [focusSignal]);

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit(code);
  }

  return (
    <AuthShell
      title={copy.title}
      subtitle={
        <>
          We sent a 6-digit code to{' '}
          <span className="dsa-dest">{maskDestination(destination)}</span>. It expires in 10
          minutes.
        </>
      }
      back={{ to: copy.backTo, label: copy.backLabel }}
      footer={<AuthFootnote>{copy.footnote}</AuthFootnote>}
    >
      <AuthBanner tone="success">{success}</AuthBanner>

      {/* The escape hatch belongs to the sentence above it, not to the cells
          below it, so it rides in the lead row rather than opening the form. */}
      <div className="dsa-authlead">
        <AuthChangeLink to={copy.changeTo}>{copy.changeLabel}</AuthChangeLink>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* The wrapper is only a focus handle. Everything visual is AuthOtp's,
            which owns the no-maxlength rule, the one-time-code attribute on
            cell one, and the always-present message region. */}
        <div ref={groupRef}>
          <AuthOtp values={values} onChange={onChange} error={error} disabled={busy} />
        </div>

        <AuthSubmit loading={busy} loadingLabel={copy.loadingLabel} disabled={!complete}>
          {copy.submitLabel}
        </AuthSubmit>
      </form>

      <AuthResend seconds={resendSeconds} onResend={onResend} disabled={resendLoading || busy} />

      <AuthHint>
        No code yet? Check your spam folder before requesting another one, and make sure the
        address above is the one on your account.
      </AuthHint>
    </AuthShell>
  );
}

export default OtpView;
